import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Icons,
  Modal,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { createStyles } from './ManageExtension.styles';
import CohortStudentsList from './CohortStudentsList';
import ExistingSubmissionConflict from './ExistingSubmissionConflict';
import ExtensionPropertiesStep from './ExtensionPropertiesStep';
import ExtensionStudentsStep from './ExtensionStudentsStep';
import {
  ExtensionAssignment,
  ExtensionGroup,
  ExtensionOperationError,
  ExtensionStudent,
  ExtensionUser,
  ManageExtensionStep,
} from './ManageExtension.types';
import TurnitinExtensionModal from './TurnitinExtensionModal';

type Props = {
  accessToken?: string;
  assignment: ExtensionAssignment;
  assignmentCreationError?: ExtensionOperationError;
  assignmentUpdateError?: ExtensionOperationError;
  createExtension: (params: any) => Promise<ExtensionAssignment | null>;
  editExtension: (params: any) => Promise<ExtensionAssignment | null>;
  extension?: ExtensionAssignment;
  isOpen: boolean;
  loadStudents: (spaceId: string) => Promise<ExtensionStudent[]>;
  onCloseModal: () => void;
  onExtensionSaved: () => Promise<void>;
  resetExtensionErrors: () => void;
  spaceId: string;
  spaceGroups: ExtensionGroup[];
  user: ExtensionUser;
};

const ManageExtension = ({
  accessToken,
  assignment,
  assignmentCreationError,
  assignmentUpdateError,
  createExtension,
  editExtension,
  extension,
  isOpen,
  loadStudents,
  onCloseModal,
  onExtensionSaved,
  resetExtensionErrors,
  spaceId,
  spaceGroups,
  user,
}: Props) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const isEditing = Boolean(extension);
  const [step, setStep] = useState<ManageExtensionStep>('students');
  const [students, setStudents] = useState<ExtensionStudent[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [extensionName, setExtensionName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [gradesReleasedAt, setGradesReleasedAt] = useState('');
  const [isHidden, setIsHidden] = useState(false);
  const [search, setSearch] = useState('');
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [turnitinAssignment, setTurnitinAssignment] =
    useState<ExtensionAssignment>();
  const [conflict, setConflict] = useState<{
    code: string;
    studentIds: string[];
  }>();

  useEffect(() => {
    if (!isOpen) return;
    resetExtensionErrors();
    setStep('students');
    setSelectedIds(extension?.students || []);
    setSelectedGroupIds([]);
    setExtensionName(extension?.title || '');
    setDescription(extension?.description ?? assignment.description ?? '');
    setStartDate(extension?.startDate || assignment.startDate || '');
    setEndDate(extension?.endDate || assignment.endDate || '');
    setGradesReleasedAt(
      extension?.gradesReleasedAt || assignment.gradesReleasedAt || ''
    );
    setIsHidden(extension?.isHidden ?? assignment.isHidden ?? false);
    setSearch('');
    setError('');
    setShowDeleteConfirmation(false);
    setTurnitinAssignment(undefined);
    setConflict(undefined);
    setIsLoadingStudents(true);
    loadStudents(spaceId)
      .then((participants) =>
        setStudents(
          participants.map((student) => ({
            ...student,
            fullName:
              student.fullName ||
              `${student.firstName || ''} ${student.lastName || ''}`.trim(),
          }))
        )
      )
      .catch(() => setError('Students could not be loaded. Please try again.'))
      .finally(() => setIsLoadingStudents(false));
  }, [
    assignment,
    extension,
    isOpen,
    loadStudents,
    resetExtensionErrors,
    spaceId,
  ]);

  useEffect(() => {
    const operationError = isEditing
      ? assignmentUpdateError
      : assignmentCreationError;
    if (!operationError) return;

    const errorCode =
      typeof operationError.errorCode === 'string'
        ? operationError.errorCode
        : operationError.errorCode?.code;
    const detail =
      operationError.detail ||
      (typeof operationError.errorCode === 'object'
        ? operationError.errorCode.detail
        : undefined);
    if (
      errorCode === 'parent-assignment-submissions-exist' ||
      errorCode === 'extension-submissions-exist'
    ) {
      setConflict({
        code: errorCode,
        studentIds: detail?.split('students: ')[1]?.split(', ') || [],
      });
    } else if (
      errorCode === 'extension-of-an-extension' ||
      operationError.errorMessage === 'extension-of-an-extension'
    ) {
      setError('An extension cannot be created from another extension.');
    } else {
      setError('The extension could not be saved. Please try again.');
    }
  }, [assignmentCreationError, assignmentUpdateError, isEditing]);

  const groupStudentIds = useMemo(
    () =>
      selectedGroupIds.flatMap((groupId) => {
        const group = spaceGroups.find(({ id }) => id === groupId);
        return (group?.students || []).map((student) =>
          typeof student === 'string' ? student : student.id
        );
      }),
    [selectedGroupIds, spaceGroups]
  );
  const assignedIds = useMemo(
    () => Array.from(new Set([...selectedIds, ...groupStudentIds])),
    [groupStudentIds, selectedIds]
  );
  const selectedStudents = useMemo(
    () => students.filter(({ id }) => assignedIds.includes(id)),
    [assignedIds, students]
  );
  const unselectedStudents = useMemo(
    () => students.filter(({ id }) => !assignedIds.includes(id)),
    [assignedIds, students]
  );
  const searchResults = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return [];
    return unselectedStudents.filter(
      ({ fullName = '', email = '' }) =>
        fullName.toLowerCase().includes(query) ||
        email.toLowerCase().includes(query)
    );
  }, [search, unselectedStudents]);

  const toggleStudent = (studentId: string) => {
    setSelectedIds((current) =>
      current.includes(studentId)
        ? current.filter((id) => id !== studentId)
        : [...current, studentId]
    );
    setSearch('');
  };

  const toggleGroup = (groupId: string) => {
    setSelectedGroupIds((current) =>
      current.includes(groupId)
        ? current.filter((id) => id !== groupId)
        : [...current, groupId]
    );
  };

  const buildExtension = (status = 'live') => ({
    ...(extension || {
      description: assignment.description,
      endDate: assignment.endDate,
      gradesReleasedAt: assignment.gradesReleasedAt,
      gradeType: assignment.gradeType,
      isAnonymised: assignment.isAnonymised,
      isHidden: assignment.isHidden,
      provider: assignment.provider,
      shouldCloseOnDueDate: assignment.shouldCloseOnDueDate,
      startDate: assignment.startDate,
    }),
    description,
    endDate: endDate || undefined,
    gradesReleasedAt: gradesReleasedAt || undefined,
    isHidden,
    ...(extension && !extension.objectId && !extension.externalId
      ? { objectId: extension.id }
      : {}),
    parentAssignmentId: extension?.parentAssignmentId || assignment.id,
    startDate: startDate || undefined,
    status,
    students: assignedIds,
    title: extensionName.trim(),
  });

  const save = async () => {
    const openDate = new Date(startDate);
    const dueDate = new Date(endDate);
    const gradesDate = gradesReleasedAt
      ? new Date(gradesReleasedAt)
      : undefined;
    if (!startDate || !endDate) {
      setError('Open date and due date are required.');
      return;
    }
    if (
      Number.isNaN(openDate.getTime()) ||
      Number.isNaN(dueDate.getTime()) ||
      dueDate < openDate
    ) {
      setError('Due date must be after the open date.');
      return;
    }
    if (
      assignment.provider === 'turnitin' &&
      (!gradesDate ||
        Number.isNaN(gradesDate.getTime()) ||
        gradesDate < dueDate)
    ) {
      setError('Grades release date must be after the due date.');
      return;
    }

    setIsSaving(true);
    setError('');
    const operation = isEditing ? editExtension : createExtension;
    try {
      const saved = await operation({
        accessToken,
        assignment: buildExtension(),
        currentUser: user,
        spaceId,
      });
      if (saved) {
        // The shared assignment reducer keeps extensions nested under their
        // parent and does not replace the nested item after an edit. Refreshing
        // is therefore required for the updated name/properties to be visible.
        // Do not turn a refresh failure into a misleading save error: the
        // extension has already been persisted at this point.
        await onExtensionSaved();
        if (saved.provider === 'turnitin') {
          setTurnitinAssignment(saved);
        } else {
          onCloseModal();
        }
      } else setError('The extension could not be saved. Please try again.');
    } catch (_error) {
      setError('The extension could not be saved. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteExtension = async () => {
    if (!extension) return;
    setIsSaving(true);
    const deleted = await editExtension({
      accessToken,
      assignment: buildExtension('trash'),
      currentUser: user,
      spaceId,
    });
    setIsSaving(false);
    if (deleted) {
      await onExtensionSaved();
      onCloseModal();
    } else setError('The extension could not be deleted. Please try again.');
  };

  const canContinue = Boolean(extensionName.trim() && assignedIds.length);
  let modalTitle = isEditing ? 'Edit extension' : 'Create extension';
  if (step === 'properties') {
    modalTitle = `Edit properties for '${extensionName}'`;
  }
  let primaryLabel = 'Next';
  if (step === 'properties') {
    primaryLabel = isEditing ? 'Apply changes' : 'Create extension';
  }

  if (conflict) {
    const affectedStudents = students.filter(({ id }) =>
      conflict.studentIds.includes(id)
    );
    return (
      <Modal open={isOpen} onClose={onCloseModal}>
        <Box sx={styles.container}>
          <ExistingSubmissionConflict
            errorCode={conflict.code}
            onClose={onCloseModal}
            provider={assignment.provider}
            students={affectedStudents}
            styles={styles}
          />
        </Box>
      </Modal>
    );
  }

  return (
    <Modal open={isOpen} onClose={onCloseModal}>
      <>
        <Box
          sx={styles.container}
          onKeyDown={(event) => event.key === 'Escape' && onCloseModal()}
        >
          <Box sx={styles.header}>
            {step === 'cohorts' ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'start',
                  gap: '8px',
                  cursor: 'pointer',
                  color: theme.palette.primary.main,
                  fontSize: '16px',
                  fontWeight: 500,
                }}
                onClick={() => setStep('students')}
              >
                <Icons.ArrowBack /> Back
              </Box>
            ) : (
              <Typography component="h2" sx={styles.title}>
                {modalTitle}
              </Typography>
            )}
            <IconButton
              aria-label="Close extension dialog"
              onClick={onCloseModal}
            >
              <Icons.Close />
            </IconButton>
          </Box>

          <Box sx={styles.content}>
            {step === 'students' && (
              <ExtensionStudentsStep
                extensionName={extensionName}
                groups={spaceGroups}
                isLoading={isLoadingStudents}
                onExtensionNameChange={setExtensionName}
                onOpenCohorts={() => setStep('cohorts')}
                onSearchChange={setSearch}
                onToggleGroup={toggleGroup}
                onToggleStudent={toggleStudent}
                search={search}
                searchResults={searchResults}
                selectedGroupIds={selectedGroupIds}
                selectedIds={assignedIds}
                selectedStudents={selectedStudents}
                studentsCount={students.length}
                styles={styles}
                unselectedCount={unselectedStudents.length}
              />
            )}

            {step === 'cohorts' && (
              <CohortStudentsList
                onToggleStudent={toggleStudent}
                selectedIds={assignedIds}
                students={students}
                styles={styles}
              />
            )}

            {step === 'properties' && (
              <ExtensionPropertiesStep
                description={description}
                endDate={endDate}
                gradesReleasedAt={gradesReleasedAt}
                isHidden={isHidden}
                isHandin={assignment.provider === 'hand-in'}
                isTurnitin={assignment.provider === 'turnitin'}
                onDescriptionChange={setDescription}
                onEndDateChange={setEndDate}
                onGradesReleasedAtChange={setGradesReleasedAt}
                onHiddenChange={setIsHidden}
                onRestore={() => {
                  setDescription(
                    extension?.description ?? assignment.description ?? ''
                  );
                  setStartDate(
                    extension?.startDate || assignment.startDate || ''
                  );
                  setEndDate(extension?.endDate || assignment.endDate || '');
                  setGradesReleasedAt(
                    extension?.gradesReleasedAt ||
                      assignment.gradesReleasedAt ||
                      ''
                  );
                  setIsHidden(
                    extension?.isHidden ?? assignment.isHidden ?? false
                  );
                }}
                onStartDateChange={setStartDate}
                originalDescription={
                  extension?.description ?? assignment.description ?? ''
                }
                originalEndDate={extension?.endDate || assignment.endDate || ''}
                originalGradesReleasedAt={
                  extension?.gradesReleasedAt ||
                  assignment.gradesReleasedAt ||
                  ''
                }
                originalIsHidden={
                  extension?.isHidden ?? assignment.isHidden ?? false
                }
                originalStartDate={
                  extension?.startDate || assignment.startDate || ''
                }
                startDate={startDate}
                styles={styles}
              />
            )}
            {error && (
              <Alert severity="error" sx={{ my: '8px' }}>
                {error}
              </Alert>
            )}
            {showDeleteConfirmation && (
              <Alert severity="warning" sx={{ my: '8px' }}>
                Delete extension &quot;{extensionName}&quot;? This cannot be
                undone.
              </Alert>
            )}
          </Box>

          <Box sx={styles.footer}>
            <Box>
              {isEditing && (
                <Button
                  type="button"
                  kind="secondary"
                  disabled={isSaving}
                  onClick={
                    showDeleteConfirmation
                      ? deleteExtension
                      : () => setShowDeleteConfirmation(true)
                  }
                >
                  {showDeleteConfirmation ? 'Confirm delete' : 'Delete'}
                </Button>
              )}
            </Box>
            <Box sx={styles.actionButtons}>
              <Button
                type="button"
                kind="secondary"
                onClick={
                  step === 'properties'
                    ? () => setStep('students')
                    : onCloseModal
                }
              >
                {step === 'properties' ? 'Back' : 'Cancel'}
              </Button>
              <Button
                type="button"
                kind="primary"
                disabled={!canContinue || isSaving}
                onClick={
                  step === 'properties' ? save : () => setStep('properties')
                }
              >
                {primaryLabel}
              </Button>
            </Box>
          </Box>
        </Box>
        {turnitinAssignment && (
          <TurnitinExtensionModal
            assignment={turnitinAssignment}
            onClose={onCloseModal}
            spaceId={spaceId}
            user={user}
          />
        )}
      </>
    </Modal>
  );
};

export default ManageExtension;
